// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import Header from "@/components/public/Header";
import { useAuth } from "@/lib/auth";
import { Check, ArrowRight, ArrowUpRight, Users, Clock } from "lucide-react";
import { useGetBillingPlans, useGetBillingStatus, usePostCheckout, usePostContactInquiry } from "@/hooks/useBilling";
import AddonPicker from "@/components/billing/AddonPicker";
import { addonForLimit, addonsMonthlyTotal, publicAddons } from "@/utils/addons.utils";
import { cn, formatCurrency } from "@/utils/common.utils";
import { PRICING_INTEREST_OPTIONS, PRICING_PLAN_FEATURES } from "@/constant/public.constant";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { FormInput, FormSelect, FormTextarea } from "@/components/forms/FormControls";
import { contactInquirySchema } from "@/schemas/public";
import { PUBLIC_ROUTES } from "@/routes/url";

// Same small-caps label the billing tab uses above its cards.
const EYEBROW = "text-[11px] font-semibold uppercase tracking-[0.16em]";

/**
 * Public pricing page, dressed in the dashboard's tokens (DM Sans, dash-*
 * surfaces, brand green + lime) so the step from here into the product does not
 * feel like changing websites.
 *
 * The plans are our own cards rather than Stripe's hosted pricing-table embed:
 * the embed renders in Stripe's fonts and chrome and cannot take ours, and it
 * repeated the same three plans shown below it. A signed-in owner's CTA opens
 * the same Checkout Session the billing tab does, so nothing was lost.
 */
export default function Pricing() {
  const { context } = useAuth();
  const { data: plansData, isLoading: plansLoading } = useGetBillingPlans();
  const plans = plansData?.plans ?? [];
  const addons = plansData?.addons ?? [];
  const { data: billing } = useGetBillingStatus({ enabled: Boolean(context?.membership) });
  const { mutateAsync: postCheckout } = usePostCheckout();
  const [pendingPlan, setPendingPlan] = useState(null);

  const role = context?.membership?.role;
  const isOwner = role === "org_owner" || role === "brokerage_admin";
  const isSubscribed = Boolean(billing?.is_subscribed);
  const orgSlug = context?.organization?.slug;

  const planById = id => plans.find(p => p.id === id);

  // Where a plan's button goes depends on who is looking. A visitor starts a
  // trial; an owner on the trial goes straight to Checkout; an owner already
  // paying is sent to the billing tab, which can quote the proration this page
  // does not know.
  const ctaFor = (planId, title) => {
    if (isOwner && isSubscribed && orgSlug) {
      return { label: "Change plan in billing", to: `/o/${orgSlug}/billing` };
    }
    if (isOwner && !isSubscribed) {
      return {
        label: `Choose ${title}`,
        onClick: async () => {
          setPendingPlan(planId);
          try {
            const { data } = await postCheckout({ plan_id: planId, origin_url: window.location.origin });
            if (data?.url) window.location.href = data.url;
          } catch {
            // usePostCheckout has already surfaced the reason.
          } finally {
            setPendingPlan(null);
          }
        },
      };
    }
    return { label: "Start free trial", to: PUBLIC_ROUTES.REGISTER };
  };

  return (
    <div className="min-h-screen bg-dash-page font-dashboard text-dash-ink">
      <Header />

      <section className="mx-auto max-w-[1180px] px-4 pb-12 pt-16 text-center sm:px-6 lg:px-8 lg:pt-20">
        <span
          className="inline-flex items-center gap-2 rounded-full border border-dash-border bg-white px-3 py-1 text-xs font-semibold text-dash-greenDeep"
          data-testid="pricing-overline"
        >
          <span className="size-1.5 rounded-full bg-dash-lime" /> Pricing
        </span>
        <h1 className="mx-auto mt-5 max-w-3xl text-4xl font-bold leading-[1.1] text-dash-ink sm:text-5xl">
          Simple pricing for the agent side of the deal.
        </h1>
        <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-dash-muted sm:text-lg">
          Only your workspace pays. Clients, lenders and closing attorneys you invite collaborate free — you never pay
          for the people you bring into a deal.
        </p>
        <ul className="mt-7 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-dash-muted">
          {["14-day free trial", "No card needed to start", "Collaborators always free"].map(point => (
            <li key={point} className="inline-flex items-center gap-1.5">
              <Check className="size-4 text-dash-greenMid" /> {point}
            </li>
          ))}
        </ul>
      </section>

      <main className="mx-auto max-w-[1180px] px-4 pb-16 sm:px-6 lg:px-8">
        <div className="grid items-stretch gap-5 md:grid-cols-3" data-testid="pricing-public-plans" id="pricing-plans">
          <PlanCard
            id="solo_monthly"
            title="Individual"
            price={planById("solo_monthly")?.amount ?? null}
            loading={plansLoading}
            blurb="For a solo agent, with an assistant seat and client access."
            features={PRICING_PLAN_FEATURES.solo_monthly}
            cta={ctaFor("solo_monthly", "Individual")}
            busy={pendingPlan === "solo_monthly"}
            disabled={pendingPlan != null}
            testId="pricing-plan-solo"
          />
          <PlanCard
            id="team_monthly"
            title="Team"
            price={planById("team_monthly")?.amount ?? null}
            loading={plansLoading}
            blurb="For a small team running deals together in one shared workspace."
            features={PRICING_PLAN_FEATURES.team_monthly}
            cta={ctaFor("team_monthly", "Team")}
            busy={pendingPlan === "team_monthly"}
            disabled={pendingPlan != null}
            highlight
            testId="pricing-plan-team"
          />
          <PlanCard
            id="growth_monthly"
            title="Growth"
            price={planById("growth_monthly")?.amount ?? null}
            loading={plansLoading}
            blurb="For established teams and boutique brokerages."
            features={PRICING_PLAN_FEATURES.growth_monthly}
            cta={ctaFor("growth_monthly", "Growth")}
            busy={pendingPlan === "growth_monthly"}
            disabled={pendingPlan != null}
            testId="pricing-plan-growth"
          />
        </div>

        <p className="mt-6 text-center text-sm text-dash-muted">
          Running a brokerage or several offices?{" "}
          <a href="#contact-form" className="inline-flex items-center gap-1 font-semibold text-dash-greenDeep hover:underline" data-testid="pricing-custom-link">
            Talk to us about a custom plan <ArrowRight className="size-3.5" />
          </a>
        </p>

        <AddonsSection addons={publicAddons(addons)} plans={plans} billing={billing} isOwner={isOwner} isSubscribed={isSubscribed} orgSlug={orgSlug} />

        <div className="mt-5 flex items-start gap-4 rounded-2xl border border-dash-border bg-white p-6" data-testid="pricing-collaborator-note">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-dash-hover text-dash-greenDeep">
            <Users className="size-5" />
          </span>
          <p className="text-sm leading-6 text-dash-muted">
            <span className="font-semibold text-dash-ink">A note on collaborators.</span> Invited clients, lenders and
            closing attorneys are never billed and never counted as seats. If we ever add premium tools specifically for
            them, it will be optional and clearly separate from your workspace subscription.
          </p>
        </div>

        <CustomPlanSection />
      </main>

      <footer className="border-t border-dash-border">
        <div className="mx-auto flex max-w-[1180px] flex-col items-start justify-between gap-3 px-4 py-8 text-sm text-dash-muted sm:px-6 md:flex-row md:items-center lg:px-8">
          <span>Questions about a plan? Use the form above and we&apos;ll follow up.</span>
          <Link to={PUBLIC_ROUTES.LANDING} className="inline-flex items-center gap-1 hover:text-dash-greenDeep" data-testid="pricing-back-home">
            Back to home <ArrowUpRight className="size-3.5" />
          </Link>
        </div>
      </footer>
    </div>
  );
}

function PlanCard({ title, price, blurb, features, cta, busy, disabled, highlight, testId, loading }) {
  return (
    <div
      className={cn(
        "flex flex-col rounded-2xl border p-6 sm:p-7",
        highlight
          ? "border-dash-greenDeep bg-dash-greenDeep text-white shadow-[0_24px_60px_-28px_rgb(var(--dash-green-deep-rgb)/0.7)]"
          : "border-dash-border bg-white"
      )}
      data-testid={testId}
    >
      <div className="flex items-center justify-between gap-3">
        <h2 className={cn("text-lg font-bold", highlight ? "text-white" : "text-dash-ink")}>{title}</h2>
        {highlight && (
          <span className="rounded-full bg-dash-lime px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-dash-sidebarDark">
            Most popular
          </span>
        )}
      </div>
      <p className={cn("mt-2 min-h-[2.5rem] text-sm leading-5", highlight ? "text-white/70" : "text-dash-muted")}>{blurb}</p>

      <div className="mt-6 flex h-12 items-baseline gap-1.5">
        {loading ? (
          // Sized to the rendered numeral so the card does not resize. Prices are
          // never hardcoded here — a placeholder amount that changes once the API
          // answers is worse than a brief blank.
          <Skeleton className={cn("h-11 w-28", highlight && "bg-white/15")} />
        ) : typeof price === "number" ? (
          <>
            <span className="text-[2.75rem] font-bold leading-none">${Math.round(price)}</span>
            <span className={cn("text-sm", highlight ? "text-white/70" : "text-dash-muted")}>/ month</span>
          </>
        ) : (
          // Resolved, but the API did not return this plan.
          <span className={cn("text-2xl font-bold", highlight ? "text-white/70" : "text-dash-muted")}>Contact us</span>
        )}
      </div>

      <PlanCta cta={cta} highlight={highlight} busy={busy} disabled={disabled} testId={`${testId}-cta`} />

      <div className={cn("my-6 h-px", highlight ? "bg-white/15" : "bg-dash-border")} />

      <ul className="flex-1 space-y-3 text-sm">
        {features.map(f => (
          <li key={f} className="flex items-start gap-2.5">
            <Check className={cn("mt-0.5 size-4 shrink-0", highlight ? "text-dash-lime" : "text-dash-greenMid")} />
            <span className={highlight ? "text-white/90" : "text-dash-ink"}>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PlanCta({ cta, highlight, busy, disabled, testId }) {
  const className = cn(
    "mt-6 h-11 w-full text-sm font-semibold",
    highlight && "!rounded-[8px] bg-dash-lime text-dash-sidebarDark shadow-none hover:bg-dash-lime hover:brightness-95"
  );
  const variant = highlight ? "default" : "dashboardOutline";
  if (cta.onClick) {
    return (
      <Button type="button" variant={variant} className={className} onClick={cta.onClick} loading={busy} disabled={disabled} data-testid={testId}>
        {cta.label}
      </Button>
    );
  }
  return (
    <Button variant={variant} className={className} asChild data-testid={testId}>
      <Link to={cta.to}>{cta.label}</Link>
    </Button>
  );
}

/**
 * Add-ons on the public pricing page.
 *
 * The point it has to make is that growing a little does not mean paying for
 * the next tier: one more teammate is one more seat, not a jump to Team.
 *
 * Deal packs are not shown here (`publicAddons`): the site does not advertise
 * how many deals a plan includes, and a "5 more active deals" line would. An
 * Individual org that reaches its cap is offered packs in-app instead.
 *
 * A signed-in owner gets the picker wired to checkout so the selection carries
 * straight into the Stripe session. Everyone else gets the prices and a route
 * to sign up — a visitor with no organization has nothing to attach an add-on
 * to, so a stepper here would only be able to lose their selection at the
 * registration step.
 */
function AddonsSection({ addons, plans, billing, isOwner, isSubscribed, orgSlug }) {
  const [draft, setDraft] = useState({});
  const { mutateAsync: postCheckout, isPending: checkoutBusy } = usePostCheckout();

  // Already subscribed? The billing tab is where a change to a live
  // subscription belongs — it can quote the proration and the renewal date,
  // neither of which this page knows.
  const canCheckoutHere = isOwner && !isSubscribed;

  const planId = useMemo(() => {
    // Match the add-ons to the plan they'd be bought alongside: the cheapest
    // self-serve plan with room for the seats already in use. Defaults to Individual,
    // which is what a picker on this page is overwhelmingly paired with.
    const seats = billing?.usage?.seats_used ?? 1;
    const fits = plans
      .filter(p => p.public_checkout && (p.max_seats == null || p.max_seats >= seats))
      .sort((a, b) => (a.amount ?? 0) - (b.amount ?? 0));
    return fits[0]?.id ?? "solo_monthly";
  }, [billing, plans]);

  if (addons.length === 0) return null;

  const monthly = addonsMonthlyTotal(addons, draft);
  const seatAddon = addonForLimit(addons, "max_seats");

  const startCheckout = async () => {
    try {
      const { data } = await postCheckout({ plan_id: planId, origin_url: window.location.origin, addons: draft });
      if (data?.url) window.location.href = data.url;
    } catch {
      // usePostCheckout has already surfaced the reason.
    }
  };

  return (
    <section className="mt-16 rounded-2xl border border-dash-border bg-white p-6 sm:p-8" data-testid="pricing-addons">
      <p className={cn(EYEBROW, "text-dash-faint")}>Need more room?</p>
      <h2 className="mt-2 max-w-2xl text-2xl font-bold text-dash-ink sm:text-3xl" data-testid="pricing-addons-headline">
        {seatAddon ? (
          <>Add a seat anytime for {formatCurrency(seatAddon.amount)}/month.</>
        ) : (
          <>Add exactly what you need, not the next tier up.</>
        )}
      </h2>
      <p className="mt-3 max-w-2xl text-sm leading-6 text-dash-muted">
        Bringing on one more teammate shouldn&apos;t mean moving up a tier. Extra seats are billed monthly on the same
        invoice as your plan, and you can drop them again at your next renewal.
      </p>

      {canCheckoutHere ? (
        <>
          <AddonPicker addons={addons} value={draft} onChange={setDraft} className="mt-6" disabled={checkoutBusy} />
          <div className="mt-6 flex flex-col gap-3 border-t border-dash-border pt-6 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-dash-muted">
              Add-ons total <span className="font-semibold text-dash-ink">{formatCurrency(monthly)}/month</span>
              {monthly > 0 && <span> on top of your plan</span>}
            </p>
            <Button
              type="button"
              variant="dashboardPrimary"
              onClick={startCheckout}
              disabled={monthly === 0 || checkoutBusy}
              loading={checkoutBusy}
              data-testid="pricing-addons-checkout"
            >
              Continue to checkout
            </Button>
          </div>
        </>
      ) : (
        <>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2" data-testid="pricing-addons-list">
            {addons.map(addon => (
              <li key={addon.id} className="rounded-xl border border-dash-border bg-dash-searchBg p-5">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm font-semibold text-dash-ink">{addon.name}</span>
                  <span className="text-xl font-bold text-dash-ink">
                    {formatCurrency(addon.amount)}
                    <span className="text-xs font-normal text-dash-muted"> / {addon.unit_label} / mo</span>
                  </span>
                </div>
                <p className="mt-2 text-sm text-dash-muted">{addon.description}</p>
              </li>
            ))}
          </ul>
          <div className="mt-6 border-t border-dash-border pt-6">
            {isSubscribed && orgSlug ? (
              <Button variant="dashboardOutline" asChild data-testid="pricing-addons-manage">
                <Link to={`/o/${orgSlug}/billing#addons`}>Manage your add-ons</Link>
              </Button>
            ) : (
              <p className="text-sm text-dash-muted">
                Add-ons are chosen at checkout, alongside your plan.{" "}
                <Link to={PUBLIC_ROUTES.REGISTER} className="font-semibold text-dash-greenDeep hover:underline" data-testid="pricing-addons-register">
                  Start free for 14 days
                </Link>{" "}
                — no card needed.
              </p>
            )}
          </div>
        </>
      )}
    </section>
  );
}

/**
 * Brokerage and Enterprise as one offering with one form. Both are quoted
 * privately and both CTAs used to scroll to the same form, so two cards only
 * asked a visitor to pick a label before they could talk to us — the form's
 * "Which fits best?" field still records the distinction for sales.
 */
function CustomPlanSection() {
  return (
    <section
      id="contact-form"
      className="mt-16 scroll-mt-24 overflow-hidden rounded-2xl border border-dash-border bg-white lg:grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]"
      data-testid="pricing-contact-plans"
    >
      <div className="bg-dash-sidebarDark p-7 text-white sm:p-10" data-testid="pricing-plan-custom">
        <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white">
          <span className="size-1.5 rounded-full bg-dash-lime" /> Brokerage &amp; Enterprise
        </span>
        <h2 className="mt-5 text-3xl font-bold leading-tight">Running a larger office? Let&apos;s shape a plan together.</h2>
        <p className="mt-4 text-sm leading-6 text-white/70">
          Priced through a private consultation around your office size, number of teams and rollout scope — whether
          that&apos;s one brokerage or several offices.
        </p>
        <ul className="mt-7 space-y-3 text-sm">
          {PRICING_PLAN_FEATURES.custom.map(f => (
            <li key={f} className="flex items-start gap-2.5">
              <Check className="mt-0.5 size-4 shrink-0 text-dash-lime" />
              <span className="text-white/90">{f}</span>
            </li>
          ))}
        </ul>
        <p className="mt-8 inline-flex items-center gap-2 text-xs text-white/60">
          <Clock className="size-3.5" /> We reply within one business day.
        </p>
      </div>

      <div className="p-7 sm:p-10">
        <ContactForm />
      </div>
    </section>
  );
}

function ContactForm() {
  const [sent, setSent] = useState(false);
  const { mutate: postContactInquiry, isPending: busy } = usePostContactInquiry();
  const form = useForm({
    resolver: zodResolver(contactInquirySchema),
    defaultValues: { name: "", email: "", company: "", plan_interest: "brokerage", team_size: "", message: "" },
  });

  const submit = (values) => postContactInquiry(values, { onSuccess: () => setSent(true) });

  if (sent) return (
    <div className="flex h-full flex-col items-center justify-center py-10 text-center" data-testid="contact-sent">
      <span className="flex size-12 items-center justify-center rounded-full bg-dash-hover text-dash-greenMid">
        <Check className="size-6" />
      </span>
      <h3 className="mt-5 text-2xl font-bold text-dash-ink">Thanks — we&apos;ll be in touch.</h3>
      <p className="mt-3 max-w-sm text-sm text-dash-muted">
        Your inquiry is with us. We&apos;ll email a follow-up within one business day.
      </p>
    </div>
  );

  return (
    <div data-testid="contact-form-section">
      <h3 className="text-xl font-bold text-dash-ink">Tell us about your office</h3>
      <p className="mt-1 text-sm text-dash-muted">A few details and we&apos;ll follow up with a private quote.</p>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(submit)} className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="contact-form" noValidate>
          <FormInput name="name" label="Name" data-testid="contact-name" required />
          <FormInput name="email" label="Work email" type="email" data-testid="contact-email" required />
          <FormInput name="company" label="Brokerage / company" data-testid="contact-company" />
          <FormInput name="team_size" label="Team size" placeholder="e.g. 25 agents" data-testid="contact-team-size" />
          <div className="sm:col-span-2"><FormSelect name="plan_interest" label="Which fits best?" options={PRICING_INTEREST_OPTIONS} data-testid="contact-plan-interest" /></div>
          <div className="sm:col-span-2"><FormTextarea name="message" label="Anything to share?" rows={4} data-testid="contact-message" /></div>
          <div className="mt-2 flex flex-col-reverse gap-3 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-dash-faint">No commitment — just a conversation.</p>
            <Button type="submit" variant="dashboardPrimary" className="h-10 px-5" loading={busy} data-testid="contact-submit">
              Request a consult
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
